import {
    Button,
    ButtonBase,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import './Retreats.scss';
import {RetreatCenter, RetreatLeader, retreatCenters, RetreatSession} from "./retreatsData";
import {formatDateRange, formatMonth, formatWeekdays, sessionStatus, SessionStatus, todayIso} from "./retreatDates";
import {CloseIcon, OpenInNewIcon, PersonIcon, PhoneIcon, PlaceIcon} from "../common/Icons";

type View = "centers" | "calendar";

const BIO_COLLAPSE_LENGTH = 160;

function StatusBadge(props: {status: SessionStatus}) {
    if (props.status === "upcoming") {
        return null;
    }
    return (
        <span className={`RetreatsStatus RetreatsStatus-${props.status}`}>
            {props.status === "ongoing" ? "trwa" : "zakończone"}
        </span>
    )
}

function LeaderInfo(props: {leader: RetreatLeader}) {
    const [expanded, setExpanded] = useState(false);
    const bio = props.leader.bio;
    const collapsible = !!bio && bio.length > BIO_COLLAPSE_LENGTH;

    return (
        <div className="RetreatsLeader">
            <Typography className="RetreatsLeaderName">
                <PersonIcon fontSize="inherit"/> {props.leader.name}
            </Typography>
            {bio &&
                <Typography className={collapsible && !expanded ? "RetreatsLeaderBio RetreatsLeaderBioCollapsed" : "RetreatsLeaderBio"}>
                    {bio}
                </Typography>
            }
            {collapsible &&
                <button type="button" className="RetreatsLinkButton" onClick={() => setExpanded(!expanded)}>
                    {expanded ? "Zwiń" : "Więcej o prowadzącym"}
                </button>
            }
        </div>
    )
}

function SessionRow(props: {session: RetreatSession, today: string}) {
    const {session} = props;
    const status = sessionStatus(session, props.today);

    return (
        <li className={`RetreatsSession RetreatsSession-${status}`}>
            <div className="RetreatsSessionDate">
                <span className="RetreatsSessionRange">{formatDateRange(session)}</span>
                <span className="RetreatsSessionWeekdays">{formatWeekdays(session)}</span>
                <StatusBadge status={status}/>
            </div>
            {(session.title || session.topic || session.note || session.leader) &&
                <div className="RetreatsSessionBody">
                    {session.title && <Typography className="RetreatsSessionTitle">{session.title}</Typography>}
                    {session.topic && <Typography className="RetreatsSessionTopic">{session.topic}</Typography>}
                    {session.leader && <LeaderInfo leader={session.leader}/>}
                    {session.note && <Typography className="RetreatsSessionNote">{session.note}</Typography>}
                </div>
            }
        </li>
    )
}

function CenterCard(props: {center: RetreatCenter, today: string, cardRef: (el: HTMLElement | null) => void}) {
    const {center} = props;
    const datesOnly = center.sessions.every(s => !s.title && !s.topic && !s.note && !s.leader);

    return (
        <section className="RetreatsCenter" ref={props.cardRef}>
            <header className="RetreatsCenterHeader">
                <div>
                    <Typography variant="h6" component="h3" className="RetreatsCenterName">{center.name}</Typography>
                    <Typography className="RetreatsCenterPlace"><PlaceIcon fontSize="inherit"/> {center.place}</Typography>
                </div>
                {center.website &&
                    <Button
                        variant="outlined"
                        size="small"
                        href={center.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        endIcon={<OpenInNewIcon/>}
                        className="RetreatsWebsiteButton"
                    >
                        Strona www
                    </Button>
                }
            </header>

            {center.intro?.map((paragraph, i) =>
                <Typography key={i} className="RetreatsParagraph">{paragraph}</Typography>
            )}

            {center.topic &&
                <blockquote className="RetreatsTopic">
                    <span className="RetreatsTopicLabel">Temat rekolekcji</span>
                    <span className="RetreatsTopicQuote">„{center.topic.quote}” <cite>({center.topic.source})</cite></span>
                    {center.topic.subtitle && <span className="RetreatsTopicSubtitle">{center.topic.subtitle}</span>}
                </blockquote>
            }

            {center.description && <Typography className="RetreatsParagraph">{center.description}</Typography>}

            {center.leader &&
                <div className="RetreatsCenterLeader">
                    <span className="RetreatsLabel">Prowadzący</span>
                    <LeaderInfo leader={center.leader}/>
                </div>
            }

            <span className="RetreatsLabel">{center.sessions.length > 1 ? "Terminy" : "Termin"}</span>
            <ul className={datesOnly ? "RetreatsSessions RetreatsSessionsDatesOnly" : "RetreatsSessions"}>
                {center.sessions.map(session =>
                    <SessionRow key={`${session.from}-${session.title ?? ""}`} session={session} today={props.today}/>
                )}
            </ul>

            {center.schedule &&
                <ul className="RetreatsSchedule">
                    {center.schedule.map(item => <li key={item}>{item}</li>)}
                </ul>
            }

            {center.signup &&
                <div className="RetreatsSignup">
                    <span className="RetreatsSignupLabel">{center.signup.label}:</span>
                    {center.signup.phones.map(phone =>
                        <a key={phone} href={`tel:${phone.replace(/\s/g, "")}`} className="RetreatsPhone">
                            <PhoneIcon fontSize="inherit"/> {phone}
                        </a>
                    )}
                </div>
            }
        </section>
    )
}

function nextSession(center: RetreatCenter, today: string): RetreatSession | undefined {
    return center.sessions.find(s => sessionStatus(s, today) !== "past");
}

function CentersView(props: {today: string, scrollTarget: string | null, onScrolled: () => void}) {
    const cardRefs = useRef<Record<string, HTMLElement | null>>({});
    const {scrollTarget, onScrolled} = props;

    const scrollTo = (id: string) => {
        cardRefs.current[id]?.scrollIntoView({behavior: "smooth", block: "start"});
    };

    useEffect(() => {
        if (scrollTarget) {
            // jump instantly - the view has just been switched, so a smooth scroll would start from an unrelated position
            cardRefs.current[scrollTarget]?.scrollIntoView({block: "start"});
            onScrolled();
        }
    }, [scrollTarget, onScrolled]);

    return (
        <div className="RetreatsCentersView">
            <nav className="RetreatsNav" aria-label="Domy rekolekcyjne">
                {retreatCenters.map(center => {
                    const next = nextSession(center, props.today);
                    return (
                        <ButtonBase key={center.id} className="RetreatsNavItem" onClick={() => scrollTo(center.id)}>
                            <span className="RetreatsNavName">{center.name}</span>
                            <span className="RetreatsNavPlace">{center.place}</span>
                            <span className="RetreatsNavNext">
                                {next ? `najbliżej: ${formatDateRange(next)}` : "brak nadchodzących terminów"}
                            </span>
                        </ButtonBase>
                    )
                })}
            </nav>
            <div className="RetreatsCenters">
                {retreatCenters.map(center =>
                    <CenterCard
                        key={center.id}
                        center={center}
                        today={props.today}
                        cardRef={el => cardRefs.current[center.id] = el}
                    />
                )}
            </div>
        </div>
    )
}

function CalendarView(props: {today: string, onShowCenter: (id: string) => void}) {
    const months = useMemo(() => {
        const entries = retreatCenters
            .flatMap(center => center.sessions.map(session => ({center, session})))
            .filter(e => sessionStatus(e.session, props.today) !== "past")
            .sort((a, b) => a.session.from.localeCompare(b.session.from) || a.center.name.localeCompare(b.center.name));

        const grouped = new Map<string, typeof entries>();
        entries.forEach(e => {
            const month = formatMonth(e.session.from);
            grouped.set(month, [...(grouped.get(month) ?? []), e]);
        });
        return Array.from(grouped.entries());
    }, [props.today]);

    if (months.length === 0) {
        return <Typography className="RetreatsEmpty">Brak nadchodzących terminów rekolekcji.</Typography>;
    }

    return (
        <div className="RetreatsCalendar">
            {months.map(([month, entries]) =>
                <section key={month} className="RetreatsMonth">
                    <Typography variant="h6" component="h3" className="RetreatsMonthName">{month}</Typography>
                    <ul>
                        {entries.map(({center, session}) =>
                            <li key={`${center.id}-${session.from}-${session.title ?? ""}`} className="RetreatsCalendarRow">
                                <div className="RetreatsSessionDate">
                                    <span className="RetreatsSessionRange">{formatDateRange(session)}</span>
                                    <span className="RetreatsSessionWeekdays">{formatWeekdays(session)}</span>
                                    <StatusBadge status={sessionStatus(session, props.today)}/>
                                </div>
                                <div className="RetreatsCalendarCenter">
                                    <button type="button" className="RetreatsLinkButton RetreatsCalendarCenterName"
                                            onClick={() => props.onShowCenter(center.id)}>
                                        {center.name}
                                    </button>
                                    <span className="RetreatsNavPlace">{center.place}</span>
                                </div>
                                <div className="RetreatsCalendarDetails">
                                    {(session.leader ?? center.leader) &&
                                        <span className="RetreatsCalendarLeader">{(session.leader ?? center.leader)!.name}</span>
                                    }
                                    {(session.topic ?? session.title ?? session.note) &&
                                        <span className="RetreatsCalendarTopic">{session.topic ?? session.title ?? session.note}</span>
                                    }
                                </div>
                            </li>
                        )}
                    </ul>
                </section>
            )}
        </div>
    )
}

function RetreatsDialog(props: {open: boolean, onClose: () => void}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [view, setView] = useState<View>("centers");
    const [scrollTarget, setScrollTarget] = useState<string | null>(null);
    const today = todayIso();

    const clearScrollTarget = useCallback(() => setScrollTarget(null), []);

    const showCenter = (id: string) => {
        setView("centers");
        setScrollTarget(id);
    };

    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            maxWidth="lg"
            fullWidth
            fullScreen={isMobile}
            scroll="paper"
            className="RetreatsDialog"
            aria-labelledby="retreats-title"
        >
            <DialogTitle id="retreats-title" className="RetreatsDialogTitle">
                <img src={require('../services/icons/icon-rekolekcje.png')} alt="" className="RetreatsDialogIcon"/>
                <span>Rekolekcje dla osób duchownych</span>
                <IconButton aria-label="Zamknij" onClick={props.onClose} className="RetreatsDialogClose">
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>

            <DialogContent dividers className="Retreats">
                <div className="RetreatsToolbar">
                    <Typography className="RetreatsLead">
                        Propozycje rekolekcji kapłańskich w domach rekolekcyjnych w całej Polsce.
                        Zgłoszenia przyjmują bezpośrednio organizatorzy.
                    </Typography>
                    <ToggleButtonGroup
                        value={view}
                        exclusive
                        size="small"
                        onChange={(e, value: View | null) => value && setView(value)}
                        className="RetreatsViewToggle"
                    >
                        <ToggleButton value="centers">Domy rekolekcyjne</ToggleButton>
                        <ToggleButton value="calendar">Kalendarz terminów</ToggleButton>
                    </ToggleButtonGroup>
                </div>

                {view === "centers"
                    ? <CentersView today={today} scrollTarget={scrollTarget} onScrolled={clearScrollTarget}/>
                    : <CalendarView today={today} onShowCenter={showCenter}/>
                }
            </DialogContent>
        </Dialog>
    )
}

export default RetreatsDialog;
